import { useAtom, useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { itemSearchQueryAtom, debouncedSearchQueryAtom } from '@/atoms'
import { allRecipes } from '@/data'
import { allRecords } from '@/data/gatheringIndex'
import type { ItemSearchResult } from '@/types'

const DEBOUNCE_MS = 300
const MAX_SUGGESTIONS = 20

const SEARCHABLE_ITEM_NAMES = Array.from(
  new Set([
    ...allRecipes.map((r) => r.result.name),
    ...allRecipes.flatMap((r) => r.hqResults.map((hq) => hq.name)),
    ...allRecipes.flatMap((r) => r.ingredients.map((ing) => ing.name)),
    ...allRecords.map((r) => r.name),
  ])
).sort((a, b) => a.localeCompare(b, 'ja'))

export function useItemSearch(): {
  query: string
  setQuery: (q: string) => void
  inputQuery: string
  setInputQuery: (q: string) => void
  result: ItemSearchResult
  suggestions: string[]
} {
  const [query, setQuery] = useAtom(itemSearchQueryAtom)
  const setDebounced = useSetAtom(debouncedSearchQueryAtom)
  const [inputQuery, setInputQuery] = useState(query)
  const [debounced, setLocalDebounced] = useState(query)

  useEffect(() => {
    setInputQuery(query)
  }, [query])

  useEffect(() => {
    const timer = setTimeout(() => {
      setLocalDebounced(query)
      setDebounced(query)
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, setDebounced])

  const result: ItemSearchResult = useMemo(() => {
    const q = debounced.trim()
    if (!q) return { query: q, canMake: [], usedIn: [], gatheredAt: [] }
    const canMake = allRecipes.filter(
      (r) => r.result.name === q || r.hqResults.some((hq) => hq.name === q)
    )
    const usedIn = allRecipes.filter((r) => r.ingredients.some((ing) => ing.name === q))
    const seenFishingAreas = new Set<string>()
    const gatheredAt = allRecords.filter((r) => {
      if (r.name !== q) return false
      if (r.gatheringType !== 'fishing') return true
      if (seenFishingAreas.has(r.area)) return false
      seenFishingAreas.add(r.area)
      return true
    })
    return { query: q, canMake, usedIn, gatheredAt }
  }, [debounced])

  const suggestions = useMemo(() => {
    const q = inputQuery.trim().toLowerCase()
    if (!q) return []

    return SEARCHABLE_ITEM_NAMES.filter((name) => name.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(q)
        const bStarts = b.toLowerCase().startsWith(q)
        if (aStarts !== bStarts) return aStarts ? -1 : 1
        return a.localeCompare(b, 'ja')
      })
      .slice(0, MAX_SUGGESTIONS)
  }, [inputQuery])

  return { query, setQuery, inputQuery, setInputQuery, result, suggestions }
}
